import { Injectable, inject } from '@angular/core';
import { Auth } from '../auth/auth';

export const ROLES = {
  MONITORISTA: 1,
  SUPERVISOR: 2,
  TECNICO: 3,
  CLIENTE: 4,
  ADMINISTRADOR: 5,
} as const;

export type RolId = (typeof ROLES)[keyof typeof ROLES];

export const ROL_NAME: Record<RolId, string> = {
  [ROLES.MONITORISTA]: 'MONITORISTA',
  [ROLES.SUPERVISOR]: 'SUPERVISOR',
  [ROLES.TECNICO]: 'TECNICO',
  [ROLES.CLIENTE]: 'CLIENTE',
  [ROLES.ADMINISTRADOR]: 'ADMINISTRADOR',
};

export const PERMISSIONS: Record<RolId, string[]> = {
  [ROLES.ADMINISTRADOR]: ['*'],
  [ROLES.SUPERVISOR]: [
    'users.view',
    'users.manage',
    'incidencias.manage',
    'incidencias.view',
    'reports.view',
  ],
  [ROLES.TECNICO]: ['incidencias.view'],
  [ROLES.CLIENTE]: ['incidencias.view', 'incidencias.create'],
  [ROLES.MONITORISTA]: ['incidencias.view', 'incidencias.update'],
};

export const ROLE_PERMISSIONS = PERMISSIONS;

export type Permission = string;

export function hasPermission(permission: Permission, userRole: RolId | undefined): boolean {
  if (userRole === undefined) return false;
  const userPerms = PERMISSIONS[userRole] || [];
  return userPerms.includes('*') || userPerms.includes(permission);
}

export function hasAnyPermission(permissions: Permission[], userRole: RolId | undefined): boolean {
  return permissions.some((p) => hasPermission(p, userRole));
}

export function hasRole(idRol: number | number[], userRole: number | undefined): boolean {
  if (userRole === undefined) return false;
  const roles = Array.isArray(idRol) ? idRol : [idRol];
  return roles.includes(userRole);
}

@Injectable({ providedIn: 'root' })
export class Permissions {
  private auth = inject(Auth);

  hasPermission(permission: Permission): boolean {
    const user = this.auth.user();
    return hasPermission(permission, user?.idRol as RolId | undefined);
  }

  hasAnyPermission(permissions: Permission[]): boolean {
    const user = this.auth.user();
    return hasAnyPermission(permissions, user?.idRol as RolId | undefined);
  }

  hasRole(idRol: number | number[]): boolean {
    const user = this.auth.user();
    return hasRole(idRol, user?.idRol);
  }

  isAdmin(): boolean {
    return this.hasRole(ROLES.ADMINISTRADOR);
  }

  isSupervisor(): boolean {
    return this.hasRole(ROLES.SUPERVISOR);
  }

  isTecnico(): boolean {
    return this.hasRole(ROLES.TECNICO);
  }

  isCliente(): boolean {
    return this.hasRole(ROLES.CLIENTE);
  }

  isMonitorista(): boolean {
    return this.hasRole(ROLES.MONITORISTA);
  }

  getRoleName(idRol: number): string {
    return ROL_NAME[idRol as RolId] || 'UNKNOWN';
  }

  getUserPermissions(): string[] {
    const user = this.auth.user();
    if (!user) return [];
    return PERMISSIONS[user.idRol as RolId] || [];
  }
}
